import create from 'zustand';

enum AuthState {
  INITIALIZING = 0,
  INITIALIZED = 1,
  NOT_SIGNED_IN = 10,
  FETCHING_USER = 20,
  SIGNED_IN = 21,
}

interface IUserData {
  userId: string;
  referenceId: string;
  createdAt: string;
  updatedAt: string;
  mainChannel: string;
  creator: boolean | null;
  grant: boolean | null;
  poseProcessingVideoIds: string[];
}

interface UserData extends IUserData {
  name: string;
  avatarUrl: string;
}

interface FetchedUserData extends IUserData {
  name: string | null;
  avatarUrl: string | null;
}

interface AuthStore {
  authState: AuthState;
  authData: Record<string, any> | null;
  userData: UserData | null;
  idToken: string | null;

  setAuthState: (authState: AuthState) => void;
  setAuthData: (authData: Record<string, any> | null) => void;
  setUserData: (userData: UserData | null) => void;
  setToken: (idToken: string | null) => void;

  isInitialized: boolean;
  isAuthenticated: boolean;
}

const useAuthStore = create<AuthStore>((set, get) => ({
  // States
  authState: AuthState.INITIALIZING,
  authData: null,
  userData: null,
  idToken: null,

  // Setters
  setAuthState(authState) {
    set({ authState });
  },
  setAuthData(authData) {
    set({ authState: AuthState.FETCHING_USER, authData });
  },
  setUserData(userData) {
    set({ authState: AuthState.SIGNED_IN, userData });
  },
  setToken(idToken) {
    set({ idToken });
  },

  // Computed states
  get isInitialized() {
    const { authState } = get();

    return authState > AuthState.INITIALIZING;
  },
  get isAuthenticated() {
    const { authState, authData, userData } = get();

    return authState === AuthState.SIGNED_IN && authData !== null && userData !== null;
  },
}));

function useInitialized() {
  return useAuthStore((state) => state.authState > AuthState.INITIALIZING);
}

function useAuthenticated() {
  return useAuthStore(
    (state) => state.authState > AuthState.INITIALIZING && state.userData !== null,
  );
}

export type { UserData, FetchedUserData };
export { useAuthStore, AuthState, useInitialized, useAuthenticated };
